import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { EMPTY, map, tap, Observable, of } from 'rxjs';
import { ArticleItem } from '../../models/article-item.model';
import { TransportMode } from '../../models/transport-mode.enum';
import { ArticleService } from '../../services/article.service';
import { LanguageService } from '../../services/language.service';
import { LabelType } from '../../models/label-type.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'hazmatsuite-next-article-selection',
  templateUrl: './article-selection.component.html',
  styleUrls: ['./article-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArticleSelectionComponent implements OnInit {
  @Input() transportMode: TransportMode = TransportMode.Unknown;
  @Input() ignoreCanBeSelectedFlag = false;
  @Input() articles: ArticleItem[] = [];
  @Input() selectedArticles: ArticleItem[] = [];
  @Input() labelType: LabelType = LabelType.Undefined;
  @Output() selectArticle = new EventEmitter<ArticleItem>();

  articles$: Observable<ArticleItem[]> = EMPTY;
  searchTranslations$ = this.languageService.translations$.pipe(
    map(data => data['SearchResource'])
  );
  accountArticleTranslations$ = this.languageService.translations$.pipe(
    map(data => data['AccountArticleResource'])
  );
  dangerousGoodListTranslations$ = this.languageService.translations$.pipe(
    map(data => data['DangerousGoodListResource'])
  );
  smartPackageTranslations$ = this.languageService.translations$.pipe(
    map(data => data['SmartPackageResource'])
  );

  filteredArticles: ArticleItem[] = [];

  filterFormGroup: FormGroup;

  constructor(private acrticleService: ArticleService, private languageService: LanguageService, private formBuilder: FormBuilder) {
    this.languageService.fetchResources(['SearchResource', 'AccountArticleResource', 'DangerousGoodListResource', 'SmartPackageResource']);
    this.filterFormGroup = this.formBuilder.group({
      filter: new FormControl('')
    });
  }

  ngOnInit(): void {
    if (this.articles.length) {
      this.articles$ = of(this.articles).pipe(
        tap((articles: ArticleItem[]) => {
          this.articles = articles.filter(a => this.selectedArticles.every(selectedArticle => selectedArticle.Id !== a.Id));
          this.filteredArticles = this.articles;
        })
      );
    } else {
      this.articles$ = this.acrticleService.getArticlesList(this.transportMode, this.labelType).pipe(
        tap((articles: ArticleItem[]) => {
          this.articles = articles.filter(a => this.selectedArticles.every(selectedArticle => selectedArticle.Id !== a.Id));
          this.filteredArticles = this.articles;
        })
      );
    }

    this.filter?.valueChanges.pipe(untilDestroyed(this)).subscribe((filterQuery: string) => {
      if (filterQuery) {
        const matcher = new RegExp(filterQuery, 'i');
        this.filteredArticles = this.articles.filter(item =>
          item.Identification.MaterialNumber?.match(matcher) ||
          item.Identification.Name?.CurrentName?.match(matcher) ||
          item.Identification.Description1?.CurrentName?.match(matcher) ||
          item.Identification.Description2?.CurrentName?.match(matcher))
      } else {
        this.filteredArticles = this.articles;
      }
    })
  }

  identify(index: number, item: ArticleItem) {
    return item.Id;
  }

  select(item: ArticleItem): void {
    this.acrticleService.selectArticle(item.Id);
    this.selectArticle.emit(item);
  }

  get filter(): AbstractControl | null {
    return this.filterFormGroup?.get('filter')
  }
}

